import React from "react";
import { blockWidth, fitFontSize, layoutCallout, wrapText } from "@/lib/chartText";
// Generate the ellipse paths
  // Not needed anymore as we're using SVG ellipse elements directlyimport React from "react";

  // Y-axis ticks
  const yTicks = [10000, 100000, 1000000, 10000000];
  const formatYTick = (tick: number) =>
    tick === 10000 ? "10 000" : tick === 100000 ? "100 000" : tick === 1000000 ? "1M" : "10M";

  export default function ResearchAutomationChart(): JSX.Element {
    // Set dimensions and margins
    const width = 1000;
    const height = 700;
    const tickFontSize = 16;
    const tickGap = 10;
    // The left margin holds the rotated axis title and the y-axis tick labels,
    // so it has to fit the longest of those labels: "10M" in English, but
    // "10 millions" in French.
    const axisTitleColumn = 30;
    const margin = {
      top: 60,
      right: 30,
      bottom: 60,
      left: Math.max(
        80,
        axisTitleColumn + blockWidth(yTicks.map(formatYTick), tickFontSize) + tickGap
      ),
    };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
  
    // Define the log scale domains
    const xMin = 8;
    const xMax = 10000;
    const yMin = 10000;
    const yMax = 10000000;
  
    // Helper function to convert from linear to log scale position
    const logScaleX = (value: number): number => {
      const logXMin = Math.log10(xMin);
      const logXMax = Math.log10(xMax);
      const logValue = Math.log10(value);
      return margin.left + ((logValue - logXMin) / (logXMax - logXMin)) * innerWidth;
    };
  
    const logScaleY = (value: number): number => {
      const logYMin = Math.log10(yMin);
      const logYMax = Math.log10(yMax);
      const logValue = Math.log10(value);
      return margin.top + innerHeight - ((logValue - logYMin) / (logYMax - logYMin)) * innerHeight;
    };
  
    // X-axis ticks
    const xTicks = [10, 100, 1000, 10000];

    const xMinorTicks = [20, 30, 40, 50, 60, 70, 80, 90, 200, 300, 400, 500, 600, 700, 800, 900, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000];
    // Add this to your yTicks array
    const yMinorTicks = [20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 2000000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 9000000];
    
    const MarColor = "rgb(11,78,16,.9)";
    const JunColor = "rgb(46,125,50,.85)";
    const SepColor = "rgb(102,187,106,.4)";

    // Define ellipse parameters for each month
    // Center coordinates and radii are in log scale units for direct SVG placement
    const rx = 570;
    const ry = 430;
    const h1 = 1.045;
    const h2 = h1*1.065;
    const v1 = 1.085;
    const v2 = v1*1.1;
    const ellipses = [
      { 
        month: "March 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx, 
        ry: ry, 
        color: MarColor
      },
      { 
        month: "June 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx*h1, 
        ry: ry*v1, 
        color: JunColor
      },
      { 
        month: "September 2027", 
        cx: logScaleX(9), 
        cy: logScaleY(11000), 
        rx: rx*h2, 
        ry: ry*v2, 
        color: SepColor
      }
    ];
  
    // Generate paths for SVG - generate ellipses cut off by axes
    const generateEllipsePath = (ellipse: {
      centerX: number,
      centerY: number,
      radiusX: number,
      radiusY: number
    }): string => {
      // Sample points around the ellipse to create the path
      const points: [number, number][] = [];
      const steps = 180; // Number of points to sample
      
      for (let i = 0; i <= steps; i++) {
        // Angle from 0 to PI (top half of ellipse)
        const angle = Math.PI * i / steps;
        
        // Calculate point on ellipse
        const x = ellipse.centerX - ellipse.radiusX * Math.cos(angle);
        const y = ellipse.centerY - ellipse.radiusY * Math.sin(angle);
        
        // Only include points within the visible range
        if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) {
          points.push([x, y]);
        }
      }
      
      // Create SVG path
      return points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${logScaleX(p[0])} ${logScaleY(p[1])}`)
        .join(" ");
    };
  
    // Define notable points on the curves
    const point1 = { x: 13*30, y: 200000, label: "200 000 copias\n30 veces\nla velocidad humana" };
    const point2 = { x: 13*50, y: 300000, label: "300 000 copias\n50 veces\nla velocidad humana" };
    const point3 = { x: 13*36.5, y: 240000, label: "" };
  
    // Define baseline human speeds for reference
    const humanSpeeds = [
      { x: 10, y: 10000, label: "Velocidad del pensamiento humano\n10 palabras/seg." },
      { x: 100, y: 10000, label: "10 veces la velocidad\ndel pensamiento humano" },
      { x: 1000, y: 10000, label: "100 veces la velocidad\ndel pensamiento humano" }
    ];
  
    // The title has nowhere to wrap to, so it shrinks to fit the chart width
    // when a translation runs longer than the English original.
    const title = "Balance del despliegue de la automatización de la investigación";
    const titleFontSize = fitFontSize(title, innerWidth, 32, 18);

    // Annotation typography and the area annotations must stay inside.
    const annotationFontSize = 16;
    const annotationLineHeight = 18;
    const plotBounds = {
      minX: margin.left,
      maxX: margin.left + innerWidth,
      minY: margin.top,
      maxY: margin.top + innerHeight,
    };

    // Boxed callouts on the two notable points. Each box is sized from the
    // text it holds, so a translation that runs longer than the English
    // original widens the box instead of spilling out of it.
    const calloutOptions = {
      fontSize: annotationFontSize,
      lineHeight: annotationLineHeight,
      padding: 6,
      gap: 6,
      bounds: plotBounds,
    };
    const callout1 = layoutCallout({
      pointX: logScaleX(point1.x),
      pointY: logScaleY(point1.y),
      text: point1.label,
      prefer: { horizontal: "left", vertical: "below" },
      ...calloutOptions,
    });
    const callout2 = layoutCallout({
      pointX: logScaleX(point2.x),
      pointY: logScaleY(point2.y),
      text: point2.label,
      prefer: { horizontal: "right", vertical: "above" },
      ...calloutOptions,
    });

    // Reference labels sit to the right of the dashed line they describe, and
    // may use the space up to the next one. They are anchored to the bottom of
    // the plot so that a label needing more lines grows upwards, away from the
    // x-axis, rather than over it.
    const speedLabelGap = 8;
    const speedLabelX = humanSpeeds.map((speed) => logScaleX(speed.x) + 48);
    const speedLabelLines = humanSpeeds.map((speed, i) => {
      const rightEdge =
        i + 1 < speedLabelX.length ? speedLabelX[i + 1] - speedLabelGap : plotBounds.maxX;
      return wrapText(speed.label, rightEdge - speedLabelX[i], annotationFontSize);
    });

    // Define colors
    const colors = {
      march: MarColor, // Dark green
      june: JunColor,  // Medium green
      september: SepColor, // Light green
      grid: "#cccccc",  // Light gray
      text: "#333333",  // Dark gray for text
      point: "#000000"  // Black for points
    };
  
    return (
      <div className="w-full relative overflow-hidden mt-8 mb-8">
        <svg
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          viewBox={`0 0 ${width} ${height}`}
        >
          {/* Chart Title */}
          <text
            x={margin.left}
            y={margin.top / 1.5}
            fontSize={titleFontSize}
            fontFamily="monospace"
            textAnchor="start"
            fill={colors.text}
            className="font-mono"
          >
            {title}
          </text>
  
          {/* Legend */}
          <g transform={`translate(${width - margin.right - 150}, ${margin.top + 20})`}>
            {/* March 2027 */}
            <line x1="0" y1="0" x2="30" y2="0" stroke={colors.march} strokeWidth="2" />
            <text x="40" y="5" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Mar 2027</text>
            
            {/* June 2027 */}
            <line x1="0" y1="25" x2="30" y2="25" stroke={colors.june} strokeWidth="2" />
            <text x="40" y="30" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Jun 2027</text>
            
            {/* September 2027 */}
            <line x1="0" y1="50" x2="30" y2="50" stroke={colors.september} strokeWidth="2" />
            <text x="40" y="55" fontSize="20" fontFamily="monospace" className="text-[20px] font-mono" fill={colors.text}>Sep 2027</text>
          </g>
  
          {/* Grid lines */}
          {/* Vertical grid lines */}
          {xTicks.map((tick) => (
            <line
              key={`vgrid-${tick}`}
              x1={logScaleX(tick)}
              y1={margin.top}
              x2={logScaleX(tick)}
              y2={margin.top + innerHeight}
              stroke={colors.grid}
              strokeWidth="1"
              opacity="0.7"
            />
          ))}
  
          {/* Horizontal grid lines */}
          {yTicks.map((tick) => (
            <line
              key={`hgrid-${tick}`}
              x1={margin.left}
              y1={logScaleY(tick)}
              x2={margin.left + innerWidth}
              y2={logScaleY(tick)}
              stroke={colors.grid}
              strokeWidth="1"
              opacity="0.7"
            />
          ))}
        {/* Minor X-axis grid lines */}
        {xMinorTicks.map((tick) => (
        <line
            key={`vgrid-minor-${tick}`}
            x1={logScaleX(tick)}
            y1={margin.top}
            x2={logScaleX(tick)}
            y2={margin.top + innerHeight}
            stroke={colors.grid}
            strokeWidth="0.5"
            opacity="0.5"
        />
        ))}

        {/* Minor Y-axis grid lines */}
        {yMinorTicks.map((tick) => (
        <line
            key={`hgrid-minor-${tick}`}
            x1={margin.left}
            y1={logScaleY(tick)}
            x2={margin.left + innerWidth}
            y2={logScaleY(tick)}
            stroke={colors.grid}
            strokeWidth="0.5"
            opacity="0.5"
        />
        ))}

        {/* Special dotted lines for x = 13, 130, and 1300 */}
        {[13.3, 133, 1330].map((tick) => (
        <line
            key={`special-${tick}`}
            x1={logScaleX(tick)}
            y1={margin.top}
            x2={logScaleX(tick)}
            y2={margin.top + innerHeight}
            stroke="#20aE20" // Light green color
            strokeWidth="1,5"
            strokeDasharray="4 4" // Creates dotted line
            opacity="0.8"
        />
        ))}
          {/* X-axis */}
          <line
            x1={margin.left}
            y1={margin.top + innerHeight}
            x2={margin.left + innerWidth}
            y2={margin.top + innerHeight}
            stroke={colors.text}
            strokeWidth="1.5"
          />
  
          {/* Y-axis */}
          <line
            x1={margin.left}
            y1={margin.top}
            x2={margin.left}
            y2={margin.top + innerHeight}
            stroke={colors.text}
            strokeWidth="1.5"
          />
  
          {/* X-axis label */}
          <text
            x={margin.left + innerWidth / 2}
            y={height-10}
            textAnchor="middle"
            fontSize="20"
            fontFamily="monospace"
            className="text-[20px] font-mono"
            fill={colors.text}
          >
            Velocidad (tokens/seg.)
          </text>
  
          {/* Y-axis label */}
          <text
            transform={`rotate(-90, ${axisTitleColumn - 6}, ${margin.top + innerHeight / 2})`}
            x={axisTitleColumn - 6}
            y={margin.top + innerHeight / 2}
            textAnchor="middle"
            fontSize="20"
            fontFamily="monospace"
            className="text-[20px] font-mono"
            fill={colors.text}
          >
            Copias paralelas
          </text>
  
          {/* X-axis ticks */}
          {xTicks.map((tick) => (
            <g key={`xtick-${tick}`}>
              <line
                x1={logScaleX(tick)}
                y1={margin.top + innerHeight}
                x2={logScaleX(tick)}
                y2={margin.top + innerHeight + 5}
                stroke={colors.text}
                strokeWidth="1.5"
              />
              <text
                x={logScaleX(tick)}
                y={margin.top + innerHeight + 25}
                textAnchor="middle"
                fontSize="16"
                fontFamily="monospace"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {tick.toLocaleString()}
              </text>
            </g>
          ))}
  
          {/* Y-axis ticks */}
          {yTicks.map((tick) => (
            <g key={`ytick-${tick}`}>
              <line
                x1={margin.left - 5}
                y1={logScaleY(tick)}
                x2={margin.left}
                y2={logScaleY(tick)}
                stroke={colors.text}
                strokeWidth="1.5"
              />
              <text
                x={margin.left - 10}
                y={logScaleY(tick) + 5}
                textAnchor="end"
                fontSize="16"
                fontFamily="monospace"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {formatYTick(tick)}
              </text>
            </g>
          ))}
  
          {/* Draw the ellipses */}
          {ellipses.map((item, index) => (
            <React.Fragment key={`ellipse-${index}`}>
              {/* Create a clipping path to cut off the ellipse at the axes */}
              <defs>
                <clipPath id={`clip-${index}`}>
                  <rect x={margin.left} y={margin.top} width={innerWidth} height={innerHeight} />
                </clipPath>
              </defs>
              
              {/* Draw the ellipse with the clipping path */}
              <ellipse
                cx={item.cx}
                cy={item.cy}
                rx={item.rx}
                ry={item.ry}
                fill="none"
                stroke={item.color}
                strokeWidth="2.5"
                clipPath={`url(#clip-${index})`}
              />
            </React.Fragment>
          ))}
  
          {/* Draw notable points */}
          {/* Point 1 on March curve */}
          <circle
            cx={logScaleX(point1.x)}
            cy={logScaleY(point1.y)}
            r="6"
            fill={"rgb(11,78,16)"}
          />
          <rect
            x={callout1.box.x}
            y={callout1.box.y}
            width={callout1.box.width}
            height={callout1.box.height}
            rx="5"
            ry="5"
            fill="rgba(255, 255, 255, 0.8)"
            stroke="#ccc"
          />
          <text
            x={callout1.textX}
            y={callout1.textY}
            textAnchor="middle"
            fontSize="16"
            fontFamily="monospace"
            className="text-[16px] font-mono"
            fill={colors.text}
          >
            {callout1.lines.map((line, i) => (
              <tspan key={i} x={callout1.textX} dy={i === 0 ? 0 : annotationLineHeight}>
                {line}
              </tspan>
            ))}
          </text>
  
          {/* Point 2 on June curve */}
          <circle
            cx={logScaleX(point2.x)}
            cy={logScaleY(point2.y)}
            r="6"
            fill={"rgb(155,211,158)"}
          />
          <rect
            x={callout2.box.x}
            y={callout2.box.y}
            width={callout2.box.width}
            height={callout2.box.height}
            rx="5"
            ry="5"
            fill="rgba(255, 255, 255, 0.8)"
            stroke="#ccc"
          />
          <text
            x={callout2.textX}
            y={callout2.textY}
            textAnchor="middle"
            fontSize="16"
            fontFamily="monospace"
            className="text-[16px] font-mono"
            fill={colors.text}
          >
            {callout2.lines.map((line, i) => (
              <tspan key={i} x={callout2.textX} dy={i === 0 ? 0 : annotationLineHeight}>
                {line}
              </tspan>
            ))}
          </text>

          <circle
            cx={logScaleX(point3.x)}
            cy={logScaleY(point3.y)}
            r="6"
            fill={"rgb(46,125,50)"}
          />
      {/* const MarColor = "rgb(11,78,16,.9)";
    const JunColor = "rgb(46,125,50,.85)";
    const SepColor = "rgb(102,187,106,.4)"; */}
          {/* Human speed reference labels */}
          {humanSpeeds.map((speed, i) => {
            const lines = speedLabelLines[i];
            const lastBaseline = plotBounds.maxY - 6;
            return (
              <g key={`speed-${i}`}>
                <text
                  x={speedLabelX[i]}
                  y={lastBaseline - (lines.length - 1) * 15}
                  textAnchor="start"
                  fontSize="16"
                  fontFamily="monospace"
                  className="text-[16px] font-mono"
                  fill={MarColor}
                >
                  {lines.map((line, j) => (
                    <tspan key={j} x={speedLabelX[i]} dy={j > 0 ? 15 : 0}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  }